/**
 * Phase 83.1 — Shared Evidence Uploader
 * =====================================
 *
 * Drop-in component used by both Trial Reports and Season Reports to
 * attach up to 3 image files per report section. Uploads go through
 * `/api/attachments/upload` (Emergent Object Storage) and the resulting
 * metadata is stored on the report document under `evidence[sectionKey]`.
 *
 * Props:
 *   items       — current array of evidence dicts (may be undefined)
 *   onChange    — called with the new array whenever items are added,
 *                 removed, or the caption changes.
 *   canEdit     — when false, the component renders a read-only gallery.
 *   testidPrefix— used to compose per-item data-testid values.
 *   max         — cap on number of items (default 3).
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import AuthImg from './devreport/presentation/AuthImg';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { UploadCloud, X, ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 25 * 1024 * 1024;  // 25 MB — matches backend cap.


export default function EvidenceUploader({
  items = [],
  onChange,
  canEdit = true,
  testidPrefix = 'evidence',
  max = 3,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const list = Array.isArray(items) ? items : [];

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are supported.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Image exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit.`);
      return;
    }
    if (list.length >= max) {
      toast.error(`Max ${max} images per section.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/attachments/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const entry = {
        url: res.data?.url,
        name: res.data?.name || file.name,
        content_type: res.data?.content_type || file.type,
        size: res.data?.size || file.size,
        caption: '',
        uploaded_at: new Date().toISOString(),
      };
      onChange([...list, entry]);
      toast.success('Image attached.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeItem = (idx) => {
    onChange(list.filter((_, i) => i !== idx));
  };

  const updateCaption = (idx, caption) => {
    onChange(list.map((it, i) => (i === idx ? { ...it, caption } : it)));
  };

  if (!canEdit && list.length === 0) return null;

  return (
    <div className="mt-3 border-t border-dashed border-gray-200 dark:border-gray-700 pt-3" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
          <ImageIcon size={12} /> Evidence ({list.length}/{max})
        </p>
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`${testidPrefix}-file-input`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={uploading || list.length >= max}
              onClick={() => inputRef.current?.click()}
              data-testid={`${testidPrefix}-upload-btn`}
            >
              <UploadCloud size={12} className="mr-1" />
              {uploading ? 'Uploading…' : 'Attach image'}
            </Button>
          </>
        )}
      </div>
      {list.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid={`${testidPrefix}-list`}>
          {list.map((it, idx) => (
            <div
              key={`${it.url}-${idx}`}
              className="border border-gray-200 dark:border-gray-700 rounded-md p-2 bg-white dark:bg-gray-900"
              data-testid={`${testidPrefix}-item-${idx}`}
            >
              <div className="relative">
                <AuthImg
                  src={it.url}
                  alt={it.name || 'evidence'}
                  className="w-full h-28 object-cover rounded-sm"
                  testId={`${testidPrefix}-image-${idx}`}
                  fallback={<div className="w-full h-28 rounded-sm bg-gray-100 dark:bg-gray-800" data-testid={`${testidPrefix}-image-placeholder-${idx}`} />}
                />
                {canEdit && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute top-1 right-1 h-6 w-6 bg-white/90 hover:bg-white text-red-600"
                    onClick={() => removeItem(idx)}
                    data-testid={`${testidPrefix}-remove-${idx}`}
                  >
                    <X size={12} />
                  </Button>
                )}
              </div>
              {canEdit ? (
                <Input
                  value={it.caption || ''}
                  onChange={(e) => updateCaption(idx, e.target.value)}
                  placeholder="Caption (optional)"
                  className="mt-2 h-7 text-xs"
                  data-testid={`${testidPrefix}-caption-${idx}`}
                />
              ) : it.caption ? (
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-300" data-testid={`${testidPrefix}-caption-ro-${idx}`}>
                  {it.caption}
                </p>
              ) : null}
              <p className="mt-1 text-[10px] text-gray-400 truncate" title={it.name}>{it.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
